import { OnInit, Component, Input, ViewChild, TemplateRef, OnChanges, Output, EventEmitter, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { makeGridColumnTemplate, tableTemplateIds, DefaultMaximumTableColumnWidth } from 'src/app/helpers/table-helpers';
import { saveLocal, loadLocal, sleepForMS } from 'src/app/helpers/helpers';
import { TableColumn, TableMenuItem, TableDotMenuItem, TableIcon } from 'src/app/helpers/table.data';
import { BaseTableData } from 'src/app/helpers/table-column-types';
import { CommonModule, DatePipe } from '@angular/common';
import { exportNestedTable } from './nested-table.export-table';
import { filterActions, FilterGroupTypes } from './../../store/filters.era';
import publicHelpers from 'globals/helpers/public-helpers';
import { listOfIdsToIdIndexMap, setIntersection } from 'globals/helpers/helpers';
import { Dictionary } from '@ngrx/entity';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SortIconComponent } from './sort-icon.component';
import { PhotoComponent } from '../photo';
import { WorkerCardComponent } from '../cards/workforce-card.component';
import { MatMenuModule } from '@angular/material/menu';
import { PaginatorComponent } from './paginator.component';
import { TrafficLightComponent } from '../misc/traffic-light.component';
import { CopyUrlComponent } from 'src/app/modules/share/copy-url.component';
import { IconComponent } from '../icon/icon.component';
import { LabelItemComponent } from '../label-marker/label-item.component';
import { TaskStatusDropdownComponent } from 'src/app/modules/tasks/task-status-dropdown.component';
import { StarsComponent } from '../misc/stars.component';

@Component({
    selector: 'nested-table',
    templateUrl: "./nested-table.html",
    imports: [MatCheckboxModule, CommonModule, SortIconComponent, PhotoComponent, DatePipe, WorkerCardComponent, MatMenuModule, PaginatorComponent, TrafficLightComponent, CopyUrlComponent, IconComponent, LabelItemComponent, TaskStatusDropdownComponent, StarsComponent],
    styleUrls: ["./nested-table.scss"],
    providers: [DatePipe],
    standalone: true,
})
export class NestedTableComponent<R extends BaseTableData> implements OnInit, OnChanges, OnDestroy {

  @ViewChild(tableTemplateIds.tableDate, { static: true }) tableDate: TemplateRef<any>
  @ViewChild(tableTemplateIds.iconText, { static: true }) iconText: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableImage, { static: true }) tableImage: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableLabels, { static: true }) tableLabels: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableText, { static: true }) tableText: TemplateRef<any>
  @ViewChild(tableTemplateIds.boldText, { static: true }) boldText: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableUpdatedText, { static: true }) tableUpdatedText: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableEmail, { static: true }) tableEmail: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableTextLights, { static: true }) tableTextLights: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableLightsMap, { static: true }) tableLightsMap: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableUser, { static: true }) tableUser: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableAccount, { static: true }) tableAccount: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableWorker, { static: true }) tableWorker: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableSocial, { static: true }) tableSocial: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableLink, { static: true }) tableLink: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableHHMM, { static: true }) tableHHMM: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableFlag, { static: true }) tableFlag: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableComments, { static: true }) tableComments: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableCheck, { static: true }) tableCheck: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableFiles, { static: true }) tableFiles: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableEntity, { static: true }) tableEntity: TemplateRef<any>
  @ViewChild(tableTemplateIds.tableStars, { static: true }) tableStars: TemplateRef<any>
  @ViewChild(tableTemplateIds.taskStatus, { static: true }) taskStatus: TemplateRef<any>

  gridTemplateColumns: string

  @Input() filtersId: keyof FilterGroupTypes
  @Input() dontClearFilters = false

  @Input() columns: TableColumn[] = []
  @Input() nestedColumns: TableColumn[] = []
  @Input() nestedData: { [id:string]: any }
  @Input() columnsById: Dictionary<TableColumn> // { [id: string]: TableColumn }
  @Input() nestedColumnsById: { [id: string]: TableColumn } 
  @Input() bigSecondColumn = false
  @Input() parent: any
  @Input() parentId: string
  @Input() nestedIds: string[] = []
  @Input() items: R[] = []
  @Input() menuItems: TableMenuItem[] = []
  @Input() menuDisabled = false
  @Input() dotMenuItems: { [itemKey:string]: TableDotMenuItem }
  @Input() defaultSelectedColumnId = ''
  @Input() defaultNestedColumnId = ''
  @Input() condensedView
  @Input() showNested = false
  @Input() nested = false
  @Input() readOnly = false
  @Input() emptyMessage = ''
  @Input() hadItemsBeforeFiltering = false
  @Input() noResultsMessage = ''
  @Input() showEmptyDiv = false

  message = ''

  itemsPerPage = 10
  @Output() selectItem: EventEmitter<R> = new EventEmitter() // technically should be a union set of all table types for the first bit, but ah well 
  @Output() updatedSelectedIds: EventEmitter<Set<string>> = new EventEmitter()
  @Output() iconClick: EventEmitter<TableIconEmitterData<R>> = new EventEmitter()
  @Output() seenBarClick: EventEmitter<R> = new EventEmitter()

  rowsOpen = 0
  openDotMenu: string
  selectedIds: Set<string> = new Set()
  columnAscendingMap: { [id:string]: boolean } = {}
  selectedColumn: string
  openedRows = {}
  helpers = publicHelpers;

  totalSelected = 0
  

  gridColumnEnd = 0
  previousSortOrder: { [id:string]: number } = {}
  pages: number[] = []
  pageIndex = 0
  visibleItems: R[] = []
  selectedPageItems = false

  constructor(
    public store: Store, 
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  ngOnDestroy() {
    if (this.filtersId && !this.dontClearFilters) {
      this.store.dispatch(filterActions.clearFilterGroup(this.filtersId))
    }
  }

  pause() {
    this.changeDetectorRef.detach()
  }
  
  resume() {
    this.changeDetectorRef.reattach()
  }

  toggleSeen(event, row: R) {
    event.stopPropagation()
    this.seenBarClick.emit(row)
  }

  toggleIcon(event, iconType: TableIcon, row: R) {
    event.stopPropagation()
    this.iconClick.emit({ iconType, row })
  }

  toggleOpenRow(item, event) {
    event.stopPropagation()
    this.openedRows[item.id] = this.openedRows[item.id] === 'expand' ? 'collapse' : 'expand'
    this.rowsOpen += (this.openedRows[item.id] === 'expand' ? 1 : -1)
    this.setTemplates()
  }

  setTemplates() {
    const noCheckboxes = this.nested || this.readOnly
    this.gridTemplateColumns = makeGridColumnTemplate(this.bigSecondColumn, this.columns.filter(c => !!c).map(c => c.maxWidth ? c.maxWidth : DefaultMaximumTableColumnWidth), noCheckboxes ? 0 : 50, this.nested, !this.nested && !!this.rowsOpen)
    this.gridColumnEnd = this.columns.length + 2 + (noCheckboxes ? 0 : 1)
    if (!this.nested) {
      const oldSize = this.selectedIds.size
      this.selectedIds = setIntersection(this.selectedIds, new Set(this.items.map(({ id }) => id)))
      if (oldSize !== this.selectedIds.size) { // must do a check here or could enter into an infinite loop
        this.updatedSelectedIds.emit(this.selectedIds)
      }
    }
  }

  ngOnChanges() {
    this.setTemplates()
    this.sortByCurrentColumn()
    this.getPages()
    this.setPageIndex()

  }

  getPages() {
    const nPages = Math.ceil(this.items.length / this.itemsPerPage)
    this.pages = [...Array(nPages).keys() ]
  }

  setVisibleRows() {
    this.visibleItems = this.items.slice(this.pageIndex*this.itemsPerPage, (this.pageIndex+1)*this.itemsPerPage)
  }

  loadItemsPerPage(isInit:boolean = false) {
    const nItems = loadLocal(`${this.filtersId || 'all'}TableItemsPerPage`) as number
    if (nItems) {
      this.itemsPerPage = nItems
      if(isInit) this.getPages();
    }
  }

  async setMessage(message: string, timeout = 5000) {
    this.message = message
    await sleepForMS(timeout)
    this.message = ''
  }



  updateItemsPerPage(nItems: number) {
    saveLocal(`${this.filtersId || 'all'}TableItemsPerPage`, nItems)
    this.loadItemsPerPage()

    this.itemsPerPage = nItems
    this.getPages()
    this.setPageIndex()
    this.sortByCurrentColumn()
  }

  setPageIndex(index: number = null) {
    if (index !== null) this.pageIndex = index
    if (this.pageIndex > this.pages.length - 1) this.pageIndex = Math.max(this.pages.length - 1, 0) // Set to last page if index beyond last page
    this.sortByCurrentColumn()
  }

  ngOnInit() {
    this.selectedColumn = this.defaultSelectedColumnId
    this.loadItemsPerPage(true)
    this.setTemplates()
    this.setPageIndex(0)
    this.sortByCurrentColumn()
    
  }

  callMethod(method: Function, clickEvent) {
    method(this.items.filter(({ id }) => this.selectedIds.has(id)), clickEvent)
  }

  callSingleMethod(method: Function, item: R, clickEvent) {
    method(item, clickEvent)
  }

  dotMenuOpened(itemId: string) {
    this.openDotMenu = itemId
    // console.log({ openDotMenu: itemId })
  }

  toggleSortByColumn({ id, sortingFunction }: TableColumn) {
    if (!sortingFunction) {
      return
    }
    const desc = !!this.columnAscendingMap[id]
    this.columnAscendingMap[id] = this.selectedColumn === id ? !desc : desc
    // saveLocal(`columnSorting`, { column, direction: this.columnDescendingMap[column] })
    this.sortByColumn(sortingFunction, id)
  }

  sortByCurrentColumn() {
    // console.log({ selectedColumn: this.selectedColumn, columnsById: this.columnsById })
    const column = this.columnsById[this.selectedColumn]
    if (!column) return // data not loaded yet
    const { id, sortingFunction } = column
    this.sortByColumn(sortingFunction, id)
  }

  sortByColumn(sortMethod: Function, columnId: string) {
    // console.log({ sortMethod, columnId })
    const mult = this.columnAscendingMap[columnId] ? 1 : -1
    // console.log(`Sorting by ${column} with multiplier ${mult}`)
    if (this.nested) {
      this.nestedIds.sort((a, b) => sortMethod({ a: this.nestedData[a], b: this.nestedData[b], mult, prev: this.previousSortOrder, parentId: this.parentId }))
      this.previousSortOrder = listOfIdsToIdIndexMap(this.nestedIds)
    } else {
      this.items.sort((a, b) => sortMethod({ a, b, mult, prev: this.previousSortOrder }))
      this.previousSortOrder = listOfIdsToIdIndexMap(this.items.map(item => item.id))
    }
    this.selectedColumn = columnId
    this.setVisibleRows()
  }

  trackById(index, item) {
    return item.id
  }

  trackByPagination(index) {
    return index % this.itemsPerPage
  }

  selectRow(item: R) {
    this.selectItem.emit(item)
  }

  selectAll() {
    this.selectedIds = new Set(this.items.map(({ id }) => id))
    this.selectedPageItems = false
  }

  unselectAll() {
    this.selectedIds = new Set()
  }

  toggleAll() {
    if (this.selectedIds.size) {
      this.selectedIds = new Set()
    } else {
      this.selectedIds = new Set(this.visibleItems.map(({ id }) => id))
      this.selectedPageItems = true
    }
    
    this.updatedSelectedIds.emit(this.selectedIds)
  }

  toggleItem(id: string, event=null) {
    this.selectedPageItems = false
    if (event) {
      event.stopPropagation()
    }
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id)
    } else {
      this.selectedIds.add(id)
    }
    this.updatedSelectedIds.emit(this.selectedIds)
  }

  exportTable(): string[][] {
    return exportNestedTable(this.columns, this.items, this.datePipe)
  }

}

export interface TableIconEmitterData<R> {
  iconType: TableIcon
  row: R
}